import { z } from "zod";
import { RecruitingTeamFitBasis } from "./design-codec";
import { fromStoragePercent } from "@/lib/skill-levels";

const FactSkillGroupSchema = z.object({
  BASICS: z.number().nullish(),
  EXPERTISE: z.number().nullish(),
  POWERS: z.number().nullish(),
  FAVORITE: z.number().nullish(),
}).passthrough();

// Stabiler Conditions-Schluessel (genesis MatchResultCriterion.conditionKey, @adr SPARK-0030-match-result-scoring-atom-derived@1) auf den
// Spark die Breakdown-Atome joint. Fehlt bei Alt-Blobs (vor #160) -> nullish, dann faellt derive
// auf JSON.stringify(condition) zurueck.
const FactSkillCriterionSchema = z.object({
  condition: z.unknown().nullish(),
  group: z.string(),
  conditionKey: z.string().nullish(),
}).passthrough();

// classification: SATISFIED|PARTIAL|VIOLATED|UNKNOWN (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2). isMust: das Muss/Kann-Atom.
// Beide fehlen bei Alt-Blobs -> nullish; derive leitet dann aus fulfillment/condition ab.
// UNKNOWN = nicht bewertbar (spark#175), z.B. Fachkompetenz ohne eingestufte Stufe. Genesis
// serialisiert das als matched=false + fulfillment=null, damit Alt-Consumer nicht brechen.
const ClassificationSchema = z.enum(["SATISFIED", "PARTIAL", "VIOLATED", "UNKNOWN"]);

const FactSkillItemSchema = z.object({
  criterion: z.unknown().nullish(),
  matched: z.boolean(),
  fulfillment: z.number().nullish(),
  actual: z.union([z.string(), z.number()]).nullish(),
  aiReason: z.string().nullish(),
  isMust: z.boolean().nullish(),
  // catch(null): ein kuenftiger fuenfter Wert darf nur dieses Feld kosten. Ohne den Fallback
  // scheitert das gesamte safeParse und parseTeamResult liefert EMPTY_VIEW, d.h. ein einzelnes
  // unbekanntes Enum wuerde das komplette Ergebnis unsichtbar machen.
  classification: ClassificationSchema.nullish().catch(null),
}).passthrough();

// Kandidatenweite AI-Einschätzung aus dem Prüfen-Lauf: Fließtext-Befunde in zwei
// Blöcken (Positiv/Negativ) + Fazit. Genesis liefert null am Member, wenn keine
// generiert wurde (AI aus, Fehler).
const AiAssessmentSchema = z.object({
  positive: z.string().nullish(),
  negative: z.string().nullish(),
  fazit: z.string().nullish(),
  suitability: z.number().nullish(),
  interviewQuestions: z.array(z.string()).default([]),
}).passthrough();

// Minimales Skill-Atom aus dem Blob (@external-adr GENESIS-0032-match-result-blob-carries-expertise-atoms): Name + Stufe (1..7). Nur bei Team-
// Ergebnissen gesetzt; null/absent in Alt-Blobs -> Skill-Struktur bleibt leer.
const ExpertiseAtomSchema = z.object({
  description: z.string().nullish(),
  level: z.number().nullish(),
}).passthrough();

const FactSkillMemberSchema = z.object({
  "personId": z.string(),
  "totalPercent": z.number().nullish(),
  items: z.array(FactSkillItemSchema).default([]),
  aiAssessment: AiAssessmentSchema.nullish(),
  expertises: z.array(ExpertiseAtomSchema).nullish(),
}).passthrough();

const FactSkillSchema = z.object({
  "totalPercent": z.number().nullish(),
  "groupResultsPercent": FactSkillGroupSchema.default({}),
  criteria: z.array(FactSkillCriterionSchema).default([]),
  "teamMembers": z.array(FactSkillMemberSchema).default([]),
}).passthrough();

const PowerGroupSchema = z.object({
  TEAM_SPIRIT: z.number().nullish(),
  COMMUNICATION: z.number().nullish(),
  RESILIENCE: z.number().nullish(),
  RESULT_ORIENTATION: z.number().nullish(),
  FUTURE_ORIENTATION: z.number().nullish(),
  INVENTIVENESS: z.number().nullish(),
}).passthrough();

const PowerCriterionSchema = z.object({
  element: z.string(),
  group: z.string(),
}).passthrough();

const PowerItemSchema = z.object({
  criterion: PowerCriterionSchema,
  value: z.number().nullish(),
}).passthrough();

const PowerMemberSchema = z.object({
  "personId": z.string(),
  "totalPercent": z.number().nullish(),
  items: z.array(PowerItemSchema).default([]),
}).passthrough();

const PowersSchema = z.object({
  "totalPercent": z.number().nullish(),
  "groupResultsPercent": PowerGroupSchema.default({}),
  criteria: z.array(PowerCriterionSchema).default([]),
  "teamMembers": z.array(PowerMemberSchema).default([]),
}).passthrough();

const ConditionResultSchema = z.object({
  condition: z.unknown().nullish(),
  "matchedCount": z.number().nullish(),
  fulfilled: z.boolean().nullish(),
}).passthrough();

const TeamMatchSchema = z.object({
  "teamSize": z.number().nullish(),
  "maleCount": z.number().nullish(),
  "femaleCount": z.number().nullish(),
  "diverseCount": z.number().nullish(),
  "minAge": z.number().nullish(),
  "maxAge": z.number().nullish(),
  "averageAge": z.number().nullish(),
  "mostFrequentJobLocationCount": z.number().nullish(),
  "mostFrequentJobDepartmentAge": z.number().nullish(),
  "conditionResults": z.array(ConditionResultSchema).default([]),
}).passthrough();

// Optimize-Infeasibility im opaken Team-Blob (genesis MatchResultInfeasibility,
// genesis #160). Zwei disjunkte Klassen: LOCKED (die gesperrten Kandidaten machen die
// Loesung unmoeglich → entsperren / Teamgroesse erhoehen; relaxations leer) und
// POOL_TOO_TIGHT (kein Team erfuellt alle MUSS → relaxations = minimum-total-shortfall
// Kulpritliste). condition ist die zurueckgemappte genesis-Condition (Label via
// __typename wie ueberall sonst), null wenn nicht mappbar → dann restrictionEnum roh.
const RelaxationSchema = z.object({
  condition: z.unknown().nullish(),
  restrictionEnum: z.string(),
  target: z.number().nullish(),
  shortfall: z.number().nullish(),
}).passthrough();

const InfeasibilitySchema = z.object({
  infeasibilityClass: z.enum(["LOCKED", "POOL_TOO_TIGHT"]),
  reason: z.string().nullish(),
  relaxations: z.array(RelaxationSchema).default([]),
}).passthrough();

// Genesis's MatchResultTeamResult is serialised with camelCase JSON keys
// (`factSkill`, `powersMatch`, `teamMatch`). The `*Result`-suffixed aliases
// (factSkillResult / powersMatchResult / teamMatchResult) are tolerated as
// fallback names in case any pre-camelCase-flip blob ever surfaces — current
// genesis emits the unsuffixed forms exclusively.
// Team-Beitrag: der Grenzbeitrag T(Bestandsteam + Kandidat) − T(Bestandsteam), ausgedrückt als
// Anteil dessen, was eine perfekte Person diesem Team noch hinzufügen könnte
// (@external-adr GENESIS-0063-team-quality-is-one-function-asked-three-questions).
// `need` beschreibt das Team selbst und ist für alle Kandidaten identisch; `top` nennt je Kandidat
// die drei Elemente, die den Beitrag tragen. Fehlt `contribution`, gibt es keinen Wert — nie 0.
const RecruitingTeamFitElement = z.object({
  element: z.string(),
  value: z.number(),
  teamStrength: z.number(),
  newStrength: z.number(),
});
export type RecruitingTeamFitElementView = z.infer<typeof RecruitingTeamFitElement>;

const RecruitingTeamFitSchema = z.object({
  basis: RecruitingTeamFitBasis,
  missingMemberIds: z.array(z.string()),
  memberCount: z.number(),
  measuredMemberCount: z.number(),
  need: z.array(z.object({
    element: z.string(),
    weight: z.number(),
    teamStrength: z.number(),
    need: z.number(),
  })).default([]),
  candidates: z.array(z.object({
    personId: z.string(),
    contribution: z.number().nullable(),
    top: z.array(RecruitingTeamFitElement).default([]),
  })),
});
export type RecruitingTeamFitView = z.infer<typeof RecruitingTeamFitSchema>;

const TeamResultBlobSchema = z.object({
  recruitingTeamFit: RecruitingTeamFitSchema.nullish().catch(null),
  "factSkill": FactSkillSchema.nullish(),
  "powersMatch": PowersSchema.nullish(),
  "teamMatch": TeamMatchSchema.nullish(),
  factSkillResult: FactSkillSchema.nullish(),
  powersMatchResult: PowersSchema.nullish(),
  teamMatchResult: TeamMatchSchema.nullish(),
  infeasibility: InfeasibilitySchema.nullish(),
  // Rohe Solver-Objective (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2: einziger gespeicherter Skalar). null solange
  // match-service sie nicht mitliefert (nicht Teil von ms#14).
  rewardValue: z.number().nullish(),
}).passthrough();

export type FactSkillGroupKey = "BASICS" | "EXPERTISE" | "POWERS" | "FAVORITE";

export type Classification = "SATISFIED" | "PARTIAL" | "VIOLATED" | "UNKNOWN";

export interface FactSkillCriterionView {
  condition: unknown;
  group: string;
  conditionKey?: string | null;
}

export interface FactSkillItemView {
  criterion: unknown;
  matched: boolean;
  fulfillment: number | null;
  actual: string | null;
  aiReason?: string | null;
  // Breakdown-Atome (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2). null/absent bei Alt-Blobs vor genesis #160.
  isMust?: boolean | null;
  classification?: Classification | null;
}

export interface AiAssessmentView {
  positive: string | null;
  negative: string | null;
  fazit: string | null;
  // Ganzheitlicher Eignungswert 0-100 aus der Genesis-AI-Bewertung.
  suitability?: number | null;
  interviewQuestions?: string[];
}

export interface ExpertiseAtomView {
  description: string | null;
  // Stufe 1..7, hier schon umgerechnet: genesis liefert die Speicher-Prozente der Expertise
  // (0..100, 0 = unbewertet), alles Weitere im Frontend rechnet in Stufen (SKILL_STRONG_LEVEL,
  // skill.levelName.*, die Kompetenz-Matrix). 0 = vorhanden, aber nicht eingestuft.
  level: number | null;
}

export interface FactSkillMemberView {
  personId: string;
  totalPercent: number;
  items: FactSkillItemView[];
  aiAssessment?: AiAssessmentView | null;
  // Volles Fachkompetenz-Inventar (@external-adr GENESIS-0032-match-result-blob-carries-expertise-atoms), nur bei Team-Ergebnissen; leer sonst.
  expertises?: ExpertiseAtomView[];
}

export interface FactSkillView {
  totalPercent: number;
  groupPercent: Partial<Record<FactSkillGroupKey, number | null>>;
  criteria: FactSkillCriterionView[];
  teamMembers: FactSkillMemberView[];
}

export type PowerGroupKey =
  | "TEAM_SPIRIT"
  | "COMMUNICATION"
  | "RESILIENCE"
  | "RESULT_ORIENTATION"
  | "FUTURE_ORIENTATION"
  | "INVENTIVENESS";

export interface PowerCriterionView {
  element: string;
  group: string;
}

export interface PowerItemView {
  criterion: PowerCriterionView;
  value: number | null;
}

export interface PowerMemberView {
  personId: string;
  totalPercent: number;
  items: PowerItemView[];
}

export interface PowersView {
  totalPercent: number;
  groupPercent: Partial<Record<PowerGroupKey, number | null>>;
  criteria: PowerCriterionView[];
  teamMembers: PowerMemberView[];
}

export interface ConditionResultView {
  condition: unknown;
  matchedCount: number;
  fulfilled: boolean;
}

export interface TeamMatchView {
  teamSize: number;
  maleCount: number;
  femaleCount: number;
  diverseCount: number;
  minAge: number | null;
  maxAge: number | null;
  averageAge: number | null;
  mostFrequentLocationCount: number;
  mostFrequentDepartmentCount: number;
  conditionResults: ConditionResultView[];
}

export type InfeasibilityClass = "LOCKED" | "POOL_TOO_TIGHT";

export interface RelaxationView {
  condition: unknown;
  restrictionEnum: string;
  target: number | null;
  shortfall: number | null;
}

export interface InfeasibilityView {
  infeasibilityClass: InfeasibilityClass;
  reason: string | null;
  relaxations: RelaxationView[];
}

export interface TeamResultView {
  recruitingTeamFit?: RecruitingTeamFitView | null;
  factSkill: FactSkillView | null;
  powers: PowersView | null;
  teamMatch: TeamMatchView | null;
  infeasibility: InfeasibilityView | null;
  rewardValue: number | null;
}

const EMPTY_VIEW: TeamResultView = {
  factSkill: null,
  powers: null,
  teamMatch: null,
  infeasibility: null,
  rewardValue: null,
};

export function parseTeamResult(blob: unknown): TeamResultView {
  if (blob === null || blob === undefined || typeof blob !== "object") return EMPTY_VIEW;
  const parsed = TeamResultBlobSchema.safeParse(blob);
  if (!parsed.success) return EMPTY_VIEW;
  const factSkill = parsed.data["factSkill"] ?? parsed.data.factSkillResult;
  const powers = parsed.data["powersMatch"] ?? parsed.data.powersMatchResult;
  const teamMatch = parsed.data["teamMatch"] ?? parsed.data.teamMatchResult;
  const infeasibility = parsed.data.infeasibility;
  return {
    recruitingTeamFit: parsed.data.recruitingTeamFit ?? null,
    factSkill: factSkill ? toFactSkillView(factSkill) : null,
    powers: powers ? toPowersView(powers) : null,
    teamMatch: teamMatch ? toTeamMatchView(teamMatch) : null,
    infeasibility: infeasibility
      ? {
          infeasibilityClass: infeasibility.infeasibilityClass,
          reason: infeasibility.reason ?? null,
          relaxations: infeasibility.relaxations.map((r) => ({
            condition: r.condition,
            restrictionEnum: r.restrictionEnum,
            target: r.target ?? null,
            shortfall: r.shortfall ?? null,
          })),
        }
      : null,
    rewardValue: parsed.data.rewardValue ?? null,
  };
}

type FactSkillData = z.infer<typeof FactSkillSchema>;
type PowersData = z.infer<typeof PowersSchema>;
type TeamMatchData = z.infer<typeof TeamMatchSchema>;

function toFactSkillView(d: FactSkillData): FactSkillView {
  return {
    totalPercent: d["totalPercent"] ?? 0,
    groupPercent: {
      BASICS: d["groupResultsPercent"]?.BASICS ?? null,
      EXPERTISE: d["groupResultsPercent"]?.EXPERTISE ?? null,
      POWERS: d["groupResultsPercent"]?.POWERS ?? null,
      FAVORITE: d["groupResultsPercent"]?.FAVORITE ?? null,
    },
    criteria: d.criteria.map((c) => ({ condition: c.condition, group: c.group, conditionKey: c.conditionKey ?? null })),
    teamMembers: d["teamMembers"].map((m) => ({
      personId: m["personId"],
      totalPercent: m["totalPercent"] ?? 0,
      items: m.items.map((i) => ({
        criterion: i.criterion,
        matched: i.matched,
        fulfillment: i.fulfillment ?? null,
        actual: i.actual == null ? null : String(i.actual),
        aiReason: i.aiReason ?? null,
        isMust: i.isMust ?? null,
        classification: i.classification ?? null,
      })),
      aiAssessment: m.aiAssessment
        ? {
            positive: m.aiAssessment.positive ?? null,
            negative: m.aiAssessment.negative ?? null,
            fazit: m.aiAssessment.fazit ?? null,
            suitability: m.aiAssessment.suitability ?? null,
            interviewQuestions: m.aiAssessment.interviewQuestions ?? [],
          }
        : null,
      expertises: (m.expertises ?? []).map((e) => ({
        description: e.description ?? null,
        // Prozent -> Stufe an der Draht-Grenze, wie ueberall sonst im Frontend (SkillLevelScale,
        // SkillsDiagram, ExpertiseSection). fromStoragePercent haelt 0/null bei 0: unbewertet
        // ist keine Stufe 1.
        level: e.level == null ? null : fromStoragePercent(e.level),
      })),
    })),
  };
}

function toPowersView(d: PowersData): PowersView {
  const teamMembers = d["teamMembers"].map((m) => ({
    personId: m["personId"],
    totalPercent: m["totalPercent"] ?? 0,
    items: m.items.map((i) => ({
      criterion: { element: i.criterion.element, group: i.criterion.group },
      value: i.value ?? null,
    })),
  }));
  return {
    // Genesis removed the aggregate totalPercent (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2: only per-element
    // items survive), so derive it from the atoms — each member's mean element
    // value, averaged over members who self-assessed. 0 iff nobody did, which is
    // exactly what the Powers-Score KPI and the "no self-assessments" hint key on.
    totalPercent: powersTeamScore(teamMembers),
    // groupResultsPercent was removed alongside totalPercent (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2), so the
    // per-cluster scores driving the Power-Clusters radar must be derived from the
    // element atoms too — mean of all self-assessed element values in each cluster.
    // null iff no element in that cluster was assessed.
    groupPercent: {
      TEAM_SPIRIT: powersGroupScore(teamMembers, "TEAM_SPIRIT"),
      COMMUNICATION: powersGroupScore(teamMembers, "COMMUNICATION"),
      RESILIENCE: powersGroupScore(teamMembers, "RESILIENCE"),
      RESULT_ORIENTATION: powersGroupScore(teamMembers, "RESULT_ORIENTATION"),
      FUTURE_ORIENTATION: powersGroupScore(teamMembers, "FUTURE_ORIENTATION"),
      INVENTIVENESS: powersGroupScore(teamMembers, "INVENTIVENESS"),
    },
    criteria: d.criteria.map((c) => ({ element: c.element, group: c.group })),
    teamMembers,
  };
}

// Mean of each member's mean element value, over members who have any self-assessed
// value. Mirrors buildHeatmapRows' per-member derivation (match-result-derive.ts).
function powersTeamScore(members: PowersView["teamMembers"]): number {
  const memberMeans = members
    .map((m) => {
      const vals = m.items.map((i) => i.value).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    })
    .filter((v): v is number => v != null);
  return memberMeans.length ? memberMeans.reduce((s, v) => s + v, 0) / memberMeans.length : 0;
}

// Mean of all self-assessed element values in one cluster, across the team.
// null when no element in that cluster was assessed (so the radar shows a gap,
// not a misleading 0).
function powersGroupScore(members: PowersView["teamMembers"], group: string): number | null {
  const vals: number[] = [];
  for (const m of members) {
    for (const i of m.items) {
      if (i.criterion.group === group && i.value != null) vals.push(i.value);
    }
  }
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
}

function toTeamMatchView(d: TeamMatchData): TeamMatchView {
  return {
    teamSize: d["teamSize"] ?? 0,
    maleCount: d["maleCount"] ?? 0,
    femaleCount: d["femaleCount"] ?? 0,
    diverseCount: d["diverseCount"] ?? 0,
    minAge: d["minAge"] ?? null,
    maxAge: d["maxAge"] ?? null,
    averageAge: d["averageAge"] ?? null,
    mostFrequentLocationCount: d["mostFrequentJobLocationCount"] ?? 0,
    // genesis field is misnamed "…DepartmentAge" but carries the department *count*
    // (mirrors mostFrequentJobLocationCount above). Not a typo — do not "fix" the key.
    mostFrequentDepartmentCount: d["mostFrequentJobDepartmentAge"] ?? 0,
    conditionResults: d["conditionResults"].map((c) => ({
      condition: c.condition,
      matchedCount: c["matchedCount"] ?? 0,
      fulfilled: c.fulfilled ?? false,
    })),
  };
}
